import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";

import {
  RESOLVE_LOCAL_AUTH_STATE,
  RESOLVE_LOCAL_USER_STATE,
} from "../auth/AuthActions";
import Config from "../../config/Config.js";

export default function ProfileChange() {
  const auth = useMemo(() => RESOLVE_LOCAL_AUTH_STATE(), []);
  const user = useMemo(() => RESOLVE_LOCAL_USER_STATE(), []);

  const [bio, setBio] = useState("");
  const [website, setWebsite] = useState("");
  const [pictureLink, setPictureLink] = useState("");
  const [cv, setCv] = useState("");

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  // --- validators ---
  const isValidUrl = (val) => {
    if (!val) return true; // optional fields
    try {
      // Accepts http(s) and bare domains; prepends http if missing scheme
      const normalized = val.match(/^https?:\/\//i) ? val : `https://${val}`;
      new URL(normalized);
      return true;
    } catch {
      return false;
    }
  };

  const normUrl = (val) => {
    if (!val) return "";
    return val.match(/^https?:\/\//i) ? val : `https://${val}`;
  };

  // --- fetch current extended profile ---
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${Config().extendedUserUrl}${user.pk}`);
        if (!active) return;
        setBio(res.data.bio || "");
        setCv(res.data.cv || "");
        setWebsite(res.data.website || "");
        setPictureLink(res.data.picture_link || "");
      } catch (err) {
        console.error("Profile Fetch Error:", err);
        console.error("Profile Fetch Error Response:", err.response);
        console.error("Profile Fetch Error Status:", err.response?.status);
        console.error("Profile Fetch Error Data:", err.response?.data);
        
        let errorMessage = "Couldn't load profile. Try refreshing.";
        if (err.response?.status === 404) {
          errorMessage = "Profile not found.";
        } else if (err.response?.status === 403) {
          errorMessage = "You don't have permission to view this profile.";
        } else if (err.response?.status === 500) {
          errorMessage = "Server error occurred while loading profile.";
        }
        
        setFeedback({
          type: "error",
          message: errorMessage,
        });
      } finally {
        if (active) setFetching(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user.pk]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!isValidUrl(website) || !isValidUrl(pictureLink)) {
      setFeedback({
        type: "error",
        message: "Please enter valid URLs for Website and Picture.",
      });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(
        `${Config().extendedUserUrl}${user.pk}/`,
        {
          user: user.pk,
          bio: bio.trim(),
          cv,
          website: normUrl(website.trim()),
          picture_link: normUrl(pictureLink.trim()),
        },
        {
          headers: { Authorization: `Token ${auth.token}` },
        }
      );

      setFeedback({ type: "success", message: "Profile saved successfully." });

      // Optional: cache a couple of useful fields locally
      if (res?.data?.website) localStorage.setItem("USER_WEBSITE", res.data.website);
      if (res?.data?.picture_link) localStorage.setItem("USER_AVATAR", res.data.picture_link);
    } catch (err) {
      console.error("Profile Save Error:", err);
      console.error("Profile Save Error Response:", err.response);
      console.error("Profile Save Error Status:", err.response?.status);
      console.error("Profile Save Error Data:", err.response?.data);
      
      let errorMessage = "Save failed. Fix the errors and try again.";
      if (err.response?.status === 400) {
        errorMessage = "Invalid profile data provided.";
      } else if (err.response?.status === 403) {
        errorMessage = "You don't have permission to update this profile.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error occurred while saving profile.";
      }
      
      setFeedback({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings__card">
      <h2 className="settings__formTitle">Profile</h2>
      <p className="settings__formSubtitle">
        Update your short bio, website, picture, and CV (Markdown).
      </p>
      <hr />

      {feedback.message && (
        <div
          className={
            feedback.type === "success"
              ? "form-alert form-alert--success"
              : "form-alert form-alert--error"
          }
        >
          {feedback.message}
        </div>
      )}

      {fetching ? (
        <div className="settings__card settings__card--muted">
          Loading profile…
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="settings__form">
          {/* Bio */}
          <div className="form-group">
            <label htmlFor="bio" className="form-label">
              Short bio
            </label>
            <input
              id="bio"
              type="text"
              className="form-control auth-form"
              placeholder="1–2 sentences about you"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Website */}
          <div className="form-group">
            <label htmlFor="website" className="form-label">
              Website
            </label>
            <input
              id="website"
              type="url"
              className="form-control auth-form"
              placeholder="yourdomain.com or https://yourdomain.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              disabled={loading}
            />
            {!isValidUrl(website) && website && (
              <small className="field-hint field-hint--error">
                Enter a valid URL (e.g., https://example.com).
              </small>
            )}
          </div>

          {/* Picture URL + Preview */}
          <div className="form-row form-row--2">
            <div className="form-group">
              <label htmlFor="picture_link" className="form-label">
                Picture (URL)
              </label>
              <input
                id="picture_link"
                type="url"
                className="form-control auth-form"
                placeholder="Link to a square image works best"
                value={pictureLink}
                onChange={(e) => setPictureLink(e.target.value)}
                disabled={loading}
              />
              {!isValidUrl(pictureLink) && pictureLink && (
                <small className="field-hint field-hint--error">
                  Enter a valid URL (e.g., https://…/avatar.png).
                </small>
              )}
            </div>

            <div className="avatar-preview">
              {pictureLink ? (
                <img
                  src={isValidUrl(pictureLink) ? normUrl(pictureLink) : ""}
                  alt="Profile preview"
                  className="avatar-preview__img"
                  onError={(e) => {
                    e.currentTarget.src =
                      "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><rect width='100%' height='100%' fill='%23f1f5f9'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%2364748b' font-size='14'>Image not available</text></svg>";
                  }}
                />
              ) : (
                <div className="avatar-preview__placeholder">No image</div>
              )}
            </div>
          </div>

          {/* CV editor + Markdown preview */}
          <div className="form-row form-row--2">
            <div className="form-group">
              <label htmlFor="cv" className="form-label">
                Curriculum Vitae (Markdown)
              </label>
              <textarea
                id="cv"
                className="form-control cv-textarea"
                rows={22}
                value={cv}
                onChange={(e) => setCv(e.target.value)}
                disabled={loading}
                placeholder={`# Your Name\n\n- Role\n- Experience bullets\n- Skills\n\n## Experience\n...`}
              />
              <small className="field-hint">
                Supports Markdown: headings, lists, links, code.
              </small>
            </div>

            <div className="markdown-preview">
              <label className="form-label">Preview</label>
              <div className="markdown-output">
                <ReactMarkdown>{cv || "_Start typing to see a preview…_"}</ReactMarkdown>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-brand" disabled={loading}>
              {loading ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
